import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import dayjs from 'dayjs';
import type { FC, ReactNode } from 'react';
import type { color } from 'types/shared/worlds';
import type { Transaction } from 'types/transactions.types';
import { resolveTransactionCounterpartyName } from 'utils/banks/banks.helpers';
import formatMoney from 'utils/formatMoney';
import { Trash2OutlineIcon, TrashIcon } from 'utils/icons';

export const revenueAlternativeTransactionsOptions = [
  {
    value: 'otherBank',
    label: 'other_revenue.payment.other_bank',
    preset: true,
  },
  {
    value: 'cash',
    label: 'other_revenue.payment.cash',
    preset: true,
  },
];

export const TransactionOption: FC<{
  transaction: Transaction;
  className?: string;
}> = ({ transaction, className }) => {
  const { amount, currency, communication, valueDate } = transaction;

  const label = resolveTransactionCounterpartyName(transaction);

  return (
    <div
      className={classNames(
        className,
        'overflow-hidden flex justify-between gap-2 text-sm leading-tight',
      )}
    >
      <div className="flex flex-col gap-0.5">
        <span className="min-w-0 break-words font-semibold text-primary">
          {label}
        </span>
        <span className="min-w-0 break-words text-primary-600">
          {communication}
        </span>
      </div>
      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
        <span className="font-semibold text-primary">
          {formatMoney(amount, currency)}
        </span>
        <span className="text-primary-600">
          {dayjs(valueDate).format('MMM D, YYYY')}
        </span>
      </div>
    </div>
  );
};

export const TransactionOption2: FC<{
  transaction: Partial<Transaction>;
  className?: string;
  onDelete?: () => void;
  isDisabled?: boolean;
}> = ({ transaction, className, onDelete, isDisabled }) => {
  const { amount, currency, communication, valueDate } = transaction;

  const label = resolveTransactionCounterpartyName(transaction);

  return (
    <div
      className={classNames(
        className,
        'overflow-hidden flex justify-between gap-4 text-sm',
      )}
    >
      <div className="flex flex-col justify-center gap-0.5 break-all">
        <span className="min-w-0 break-words font-semibold text-primary">
          {label}
        </span>
        {!!communication && (
          <span className="min-w-0 break-words text-primary-600">
            {communication}
          </span>
        )}
        {!!valueDate && (
          <span className="text-primary-600">
            {dayjs(valueDate).format('MMM D, YYYY')}
          </span>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        {amount != null && (
          <span className="font-semibold text-primary">
            {formatMoney(amount, currency)}
          </span>
        )}
        {!isDisabled && !!onDelete && (
          <Button
            asDefault
            className="bg-white rounded-lg border border-primary-100 p-1 transition-colors shadow-sm hover:bg-[#DC2626] hover:text-white focus:bg-[#DC2626] focus:text-white"
            onClick={onDelete}
          >
            <Trash2OutlineIcon className="w-6" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const TransactionOptionPreset: FC<{
  label: string;
  className?: string;
}> = ({ label, className }) => {
  return (
    <span className={classNames('text-sm font-semibold', className)}>
      {label}
    </span>
  );
};

export const TransactionGroup: FC<{ title: string; subTitle: string }> = ({
  title,
  subTitle,
}) => {
  return (
    <div className="py-2">
      <p className="text-lg text-semibold text-primary">{title}</p>
      <p className="text-sm">{subTitle}</p>
    </div>
  );
};

type SelectedOptionContainerProps = {
  onDelete: () => void;
  selectedOptionUI: ReactNode;
  isDisabled?: boolean;
  color?: color;
  className?: string;
};

export const SelectedOptionContainer: FC<SelectedOptionContainerProps> = ({
  onDelete,
  selectedOptionUI,
  isDisabled,
  color,
  className,
}) => {
  return (
    <div
      className={classNames(
        'bg-primary-25 px-4 py-3.5 rounded-md text-main flex gap-6',
        className,
      )}
    >
      {selectedOptionUI}
      <Button
        iconOnly
        structure="text"
        className="!p-2 self-start"
        color={color}
        onClick={onDelete}
        disabled={isDisabled}
      >
        <TrashIcon
          className={classNames('w-6', { 'text-gray-400': isDisabled })}
        />
      </Button>
    </div>
  );
};
